import { useEffect, useState } from 'react';
import { Upload as UploadAnt, Button } from 'antd';
import { RcFile } from 'antd/lib/upload';
import EmptyPng from './empty.png';
import { styled } from '@stitches/configed';
import { Space } from 'antd';
import { message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
  Photo,
  uploadFile,
  uploadImage,
  uploadPhoto,
} from '../../../services/attachment';
import { Image } from '../Image';
import { CropOrNotWrapper } from './CronOrNotWrapper';
import { FileDocFillIcon, FilePdfFillIcon } from '../Icons';

function getBase64(
  img: RcFile,
  callback: (r: string | ArrayBuffer | null) => void,
) {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
}
const UploadWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  '@xs': {
    flexDirection: 'row',
  },

  '& > div + div': {
    marginTop: 16,
    '@xs': {
      marginTop: 0,
      marginLeft: 16,
    },
  },
});
const ButtonUpload = styled(Button, {
  marginBottom: 20,
  color: '#021337 !important',
  backgroundColor: '#E8EAED !important',
  borderColor: '#E8EAED !important',
  padding: '9px 16px',
  display: 'flex !important',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  borderRadius: '6px !important',
  boxShadow: 'none !important',
});

const ImagePreviewWrapperStyled = styled('div', {
  width: '100%',
  height: 'fit-content',
  display: 'flex',
  justifyContent: 'center',
  '@xs': {
    width: 'auto',
  },
});

const FileCommonPreview = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',

  '& > div': {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '$bg-secondary',
  },

  '& > p': {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',

    padding: '4px 10px',
    backgroundColor: '#EBEDF4',
    marginBottom: 0,
  },
});

const fileImageExtensions = ['svg', 'jpg', 'jpeg', 'png'];

type Props = {
  type: 'photo' | 'photos' | 'image' | 'file';
  role: 'customer' | 'shipper' | 'merchant' | 'admin';
  crop?: boolean | { width: number; height: number };
  loading?: boolean;
  onUploadSuccess?: (imageUrl: string | Photo) => void;
  onUploadding?: () => void;
  onUploadFinished?: () => void;
  onChose?: (file: RcFile, imageUrl: string) => void;
  onRemove: () => void;
  onError?: (errors: string[]) => void;
  initialValue?: string | null;
  width?: number;
  height?: number;
  description?: any;
  disable?: boolean;

  accept?: string;
  fileSize?: number | false;
  fixedWidth?: boolean;
};
const Upload = ({
  type,
  role,
  crop,
  loading,
  initialValue,
  width,
  height,
  onUploadSuccess,
  onUploadding,
  onUploadFinished,
  onChose,
  onRemove,
  onError,
  description,
  disable = false,

  accept = '.jpg,.jpeg,.png,.svg',
  fileSize = 2,
  fixedWidth,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<{
    data: RcFile | undefined;
    info: {
      name: string | undefined;
      extension: string | undefined;
      url?: string | null;
    };
  }>();

  function beforeUpload(file: RcFile) {
    if (fileSize && file.size > fileSize * 1024 * 1024) {
      if (onError) {
        onError([`Hình ảnh phải nhỏ hơn ${fileSize}MB!`]);
      } else {
        message.error(`Hình ảnh phải nhỏ hơn ${fileSize}MB!`);
      }
      return false;
    }



    const upload = async (file: {
      data: RcFile;
      info: {
        name: string | undefined;
        extension: string | undefined;
        url?: string | null;
      };
    }) => {
      console.log('file', file);
      setIsLoading(true);
      onUploadding && onUploadding(); // parent
      setFile(file);
      if (onUploadSuccess) {
        try {
          const formData = new FormData();
          formData.append('file', file.data);
          if (type === 'image') {
            const resUpload = await uploadImage(role, formData);
            onUploadSuccess(resUpload.data.data.url);
          }
          if (type === 'photo') {
            const resUpload = await uploadPhoto(role, formData);
            onUploadSuccess({
              thumbnail: resUpload.data.data.thumbnail,
              image: resUpload.data.data.image,
            });
          }
          if (type === 'file') {
            if (fileImageExtensions.includes(file.info.extension || '')) {
              const resUpload = await uploadImage(role, formData);
              onUploadSuccess(resUpload.data.data.url);
            } else {
              const resUpload = await uploadFile(role, formData);
              onUploadSuccess(resUpload.data.data.url);
            }
          }
        } catch (error) {
          setFile(undefined);
          message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
        } finally {
          setIsLoading(false);
          onUploadFinished && onUploadFinished(); // parent
        }
      }
    };

    if (fileImageExtensions.includes(getFileExtension(file.name))) {
      getBase64(file, async (imageUrl) => {
        if (typeof imageUrl === 'string') {
          const newFile = {
            data: file,
            info: {
              name: file.name,
              url: imageUrl,
              extension: file.name.split('.').at(-1),
            },
          };
          onChose && onChose(file, imageUrl);
          await upload(newFile);
        }
      });
    } else {
      const newFile = {
        data: file,
        info: {
          name: file.name,
          extension: file.name.split('.').at(-1),
        },
      };
      upload(newFile);
    }

    return false;
  }

  useEffect(() => {
    setFile({
      data: undefined,
      info: {
        url: initialValue,
        name: initialValue?.split('/').pop(),
        extension: getFileExtension(initialValue || ''),
      },
    });
  }, [initialValue]);
  return (
    <UploadWrapper>
      <ImagePreviewWrapperStyled>
        {!file?.info.extension ||
          fileImageExtensions.includes(file.info.extension || '') ? (
          <Image
            key={file?.info.url ? file.info.url : EmptyPng}
            src={file?.info.url ? file.info.url : EmptyPng}
            alt={file?.info.url ? file.info.url : ''}
            style={{
              objectFit: 'cover',
              height: height || 200,
              width: fixedWidth
                ? width
                : width && height && height !== 200
                  ? width / (height / 200)
                  : 200, // convert width follow default height
              borderRadius: 10,
            }}
          />
        ) : (
          <FileCommonPreview style={{ width: width ?? 200, height: height ?? 200 }}>
            <div>
              {file.info.extension === 'doc' ? (
                <FileDocFillIcon style={{ fontSize: 32 }} />
              ) : file?.info.extension === 'pdf' ? (
                <FilePdfFillIcon style={{ fontSize: 32 }} />
              ) : (
                <></>
              )}
            </div>
            <p>{file?.info.name}</p>
          </FileCommonPreview>
        )}
      </ImagePreviewWrapperStyled>
      <Space align="start" direction="vertical">
        <CropOrNotWrapper
          enable={!!crop}
          width={typeof crop === 'object' ? crop.width : width}
          height={typeof crop === 'object' ? crop.height : height}
        >
          <UploadAnt
            accept={accept}
            showUploadList={false}
            beforeUpload={beforeUpload}
          >
            <ButtonUpload
              loading={isLoading || loading}
              disabled={isLoading || disable}
            >
              <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1.93587H1.33333V12.6025H10.6667V4.60254H8V1.93587ZM0 1.26387C0 0.898539 0.298 0.602539 0.666 0.602539H8.66667L12 3.93587V13.2645C12.0006 13.3521 11.984 13.4389 11.951 13.52C11.9181 13.6011 11.8695 13.675 11.808 13.7373C11.7466 13.7997 11.6734 13.8493 11.5928 13.8833C11.5121 13.9174 11.4255 13.9353 11.338 13.9359H0.662C0.486919 13.9347 0.319345 13.8646 0.195478 13.7409C0.0716119 13.6171 0.00139643 13.4496 0 13.2745V1.26387ZM6.66667 7.26921V9.93587H5.33333V7.26921H3.33333L6 4.60254L8.66667 7.26921H6.66667Z" fill="#021337" />
              </svg>

              Tải ảnh
            </ButtonUpload>
          </UploadAnt>
        </CropOrNotWrapper>
        {(file?.data || file?.info.url) && (
          <Button
            className="light danger"
            style={{ width: 200 }}
            icon={<DeleteOutlined />}
            onClick={() => {
              onRemove();
              setFile(undefined);
            }}
            disabled={disable}
          >
            Xoá ảnh
          </Button>
        )}
        <div>
          {description ? (
            description
          ) : (
            <>
              <div>
                -{' '}
                {width && height
                  ? `Kích thước ảnh ${Math.round((width / height) * 100) / 100
                  }:1`
                  : 'Ảnh khổ vuông'}
              </div>
              <div>- Kích thước 700px * 700px</div>
              <div>- Định dạng .jpg, .png</div>
            </>
          )}
        </div>
      </Space>
    </UploadWrapper>
  );
};

const getFileExtension = (filename: string) => {
  return filename.split('.').at(-1) || '';
};
export default Upload;
